/**
 * Klartext zu den kuratierten Slugs der Pipeline.
 *
 * ÜBERGANGSTABELLE, wie seinerzeit die Parteifarben: Die Slugs kommen aus
 * kuratierten Tabellen der Pipeline ('instrument', 'zustaendigkeit',
 * 'ergebnisKlasse', 'urheberArt') — ihre Bezeichnungen gehören dorthin, sonst
 * sind sie in jedem Portal einzeln zu pflegen. Als Anforderung notiert; bis
 * dahin steht die Zuordnung hier, klar gekennzeichnet.
 *
 * Regel dabei: Ein unbekannter Slug wird SICHTBAR durchgereicht, nie geraten
 * und nie verschluckt. Fällt etwas Neues aus der Quelle heraus, sieht man es.
 */

/** Unsere Einordnung des Instruments (nicht 'paperTypeText' — das ist die Quelle). */
const INSTRUMENT: Record<string, string> = {
  beschlussvorlage: "Beschlussvorlage",
  antrag: "Antrag",
  mitteilung: "Mitteilung",
  beantwortung: "Beantwortung",
  anfrage: "Anfrage",
  aenderungsantrag: "Änderungsantrag",
  dringlichkeitsvorlage: "Dringlichkeitsvorlage",
  dringlichkeitsantrag: "Dringlichkeitsantrag",
  unterrichtung: "Unterrichtung",
  stellungnahme: "Stellungnahme",
  eilentscheidung: "Eilentscheidung",
  "aktuelle-stunde": "Aktuelle Stunde",
  einwohneranfrage: "Einwohneranfrage",
};

/** Funktion einer Station im Beratungsweg. */
const ZUSTAENDIGKEIT: Record<string, string> = {
  entscheidung: "Entscheidung",
  vorberatung: "Vorberatung",
  anhoerung: "Anhörung",
  kenntnisnahme: "Kenntnisnahme",
  "antrag-anfrage": "Antrag oder Anfrage",
  "genehmigung-dringlichkeit": "Genehmigung der Dringlichkeit",
};

/** Ebene, auf der die Sache zuständig behandelt wird. */
const EBENE: Record<string, string> = {
  rat: "Rat",
  ausschuss: "Fachausschuss",
  bv: "Bezirksvertretung",
};

/** Wer die Sache eingebracht hat — die Art, nicht die einzelne Fraktion. */
const URHEBER_ART: Record<string, string> = {
  verwaltung: "Verwaltung",
  fraktion: "Fraktion",
  /*
   * EIGENES WORT, nicht noch einmal "Fraktion": Die Quelle nennt hier eine
   * Fraktion als Absender, sagt aber nicht welche. Zweimal dasselbe Wort mit
   * verschiedenen Zahlen (692 / 319) liest sich als Darstellungsfehler, und
   * es verdeckt den Unterschied, auf den es ankommt — nur unter den benannten
   * gibt es die zweite Stufe mit den einzelnen Fraktionen.
   */
  "fraktion-ungenannt": "Fraktion (nicht genannt)",
  "fraktionen-gemeinsam": "Mehrere Fraktionen",
  einwohner: "Einwohneranfrage",
  /*
   * Die Quelle sagt hier ausdrücklich, dass keine Fraktion dahintersteht.
   * Als "Fraktion" geführt wäre es schlicht falsch — deshalb ein eigener Wert
   * und ein eigenes Wort.
   */
  parteilos: "Parteilos",
};

/** Ergebnisklassen — nur für Filter-Beschriftungen; angezeigt wird 'result'. */
const ERGEBNIS_KLASSE: Record<string, string> = {
  "kenntnis-genommen": "Kenntnis genommen",
  beschlossen: "Beschlossen",
  vertagt: "Vertagt",
  zurueckgestellt: "Zurückgestellt",
  "geaendert-beschlossen": "Geändert beschlossen",
  empfohlen: "Empfohlen",
  erledigt: "Erledigt",
  abgelehnt: "Abgelehnt",
  ueberwiesen: "Überwiesen",
  zurueckgezogen: "Zurückgezogen",
  "ohne-votum": "Ohne Votum",
  "geaendert-empfohlen": "Geändert empfohlen",
  abgesetzt: "Abgesetzt",
  "alternative-beschlossen": "Alternative beschlossen",
  "unter-vorbehalt-beschlossen": "Unter Vorbehalt beschlossen",
};

function auflösen(tabelle: Record<string, string>, slug: string | undefined | null): string {
  if (!slug) return "";
  return tabelle[slug] ?? slug;
}

export const instrumentText = (slug?: string | null) => auflösen(INSTRUMENT, slug);
export const zustaendigkeitText = (slug?: string | null) => auflösen(ZUSTAENDIGKEIT, slug);
export const ebeneText = (slug?: string | null) => auflösen(EBENE, slug);
export const urheberArtText = (slug?: string | null) => auflösen(URHEBER_ART, slug);
export const ergebnisKlasseText = (slug?: string | null) => auflösen(ERGEBNIS_KLASSE, slug);

/** Die Instrumente in der Reihenfolge der Tabelle — für die Filterliste. */
export const INSTRUMENT_SLUGS = Object.keys(INSTRUMENT);
export const URHEBER_ART_SLUGS = Object.keys(URHEBER_ART);
export const EBENE_SLUGS = Object.keys(EBENE);
export const ERGEBNIS_KLASSE_SLUGS = Object.keys(ERGEBNIS_KLASSE);
